import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { CRICKET_API_URL } from "../config";
import {
    getResolvedLineupStatus,
    isLineupOutForMatch,
    normalizeLineupStatus,
} from "../utils/playerLineupStatus";

const parseMatchTime = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
        const normalizedValue = value < 10000000000 ? value * 1000 : value;
        const parsed = new Date(normalizedValue);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value === "string") {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed;

        const fallback = new Date(value.replace(" ", "T"));
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    }

    return null;
};

const getPlayerId = (player = {}) =>
    String(
        player?.id ||
        player?.playerId ||
        player?.pid ||
        player?._id ||
        ""
    );

const buildPlayerLookupKey = (player = {}) =>
    `${String(player?.name || "").trim().toLowerCase()}::${String(player?.team || "").trim().toUpperCase()}`;

const isLineupRestrictedStatus = (status = "") => {
    const normalizedStatus = String(status || "").trim().toLowerCase();
    return normalizedStatus === "lineup_out" || normalizedStatus === "live";
};

const normalizeRole = (role = "") => {
    const rawRole = String(role || "").toUpperCase();

    if (rawRole.includes("WK") || rawRole.includes("KEEPER")) return "WK";
    if (rawRole.includes("ALL") || rawRole.includes("AR") || rawRole.includes("ROUNDER")) return "AR";
    if (rawRole.includes("BOWL")) return "BOWL";
    if (rawRole.includes("BAT")) return "BAT";
    return "BAT";
};

const normalizeStatNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;

    const parsedValue = Number.parseFloat(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
};

const formatStatNumber = (value) => {
    const normalizedValue = normalizeStatNumber(value) ?? 0;
    return Number.isInteger(normalizedValue)
        ? normalizedValue
        : Number(normalizedValue.toFixed(1));
};

const getPlayerSelectionPercentage = (player = {}) =>
    normalizeStatNumber(
        player?.selectionPercentage ??
        player?.selectionPercent ??
        player?.selectedByPercentage ??
        player?.sel
    ) ?? 0;

const getPlayerCaptainPercentage = (player = {}) =>
    normalizeStatNumber(
        player?.captainPercentage
    ) ?? 0;

const getPlayerViceCaptainPercentage = (player = {}) =>
    normalizeStatNumber(
        player?.viceCaptainPercentage
    ) ?? 0;

const getPlayerTotalSeriesPoints = (player = {}) =>
    normalizeStatNumber(
        player?.totalSeriesPoints ??
        player?.seriesPoints ??
        player?.totalPoints ??
        player?.points
    ) ?? 0;

const buildPlayersToPass = (players = []) =>
    (players || []).map((player) => {
        const selectionPercentage = getPlayerSelectionPercentage(player);
        const totalPoints = getPlayerTotalSeriesPoints(player);

        return {
            ...player,
            sel: selectionPercentage,
            selectionPercentage,
            captainPercentage: getPlayerCaptainPercentage(player),
            viceCaptainPercentage: getPlayerViceCaptainPercentage(player),
            totalPoints
        };
    });

const getCreateTeamSortValue = (player = {}, field = "") => {
    if (field === "sel") return getPlayerSelectionPercentage(player);
    if (field === "points") return getPlayerTotalSeriesPoints(player);

    return normalizeStatNumber(player?.[field]) ?? 0;
};

const getShortTeamCode = (...candidates) => {
    for (const candidate of candidates) {
        const normalizedCandidate = String(candidate || "").trim();
        if (!normalizedCandidate) continue;

        if (normalizedCandidate.length <= 4 && normalizedCandidate === normalizedCandidate.toUpperCase()) {
            return normalizedCandidate;
        }

        const wordBasedCode = normalizedCandidate
            .split(/\s+/)
            .map((word) => word[0] || "")
            .join("")
            .toUpperCase()
            .slice(0, 3);

        if (wordBasedCode) {
            return wordBasedCode;
        }

        const slicedCode = normalizedCandidate.slice(0, 3).toUpperCase();
        if (slicedCode) {
            return slicedCode;
        }
    }

    return "T1";
};

const getCreateTeamLineupRank = (player = {}) => {
    const lineupStatus = getResolvedLineupStatus(player);

    if (lineupStatus === "PLAYING") return 0;
    if (lineupStatus === "SUBSTITUTE") return 1;
    return 2;
};

const isActiveLineupPlayer = (player = {}) => {
    const lineupStatus = getResolvedLineupStatus(player);

    return lineupStatus === "PLAYING" || lineupStatus === "SUBSTITUTE";
};

const getCreateTeamPlayerStatusItem = (player = {}) => {
    const lineupStatus = getResolvedLineupStatus(player);

    if (lineupStatus === "PLAYING") {
        return { key: "playing", label: "Playing", color: "#198754" };
    }

    if (lineupStatus === "SUBSTITUTE") {
        return { key: "substitute", label: "Substitute", color: "#0d6efd" };
    }

    if (player?.playedLastMatch) {
        return { key: "played-last-match", label: "Played last match", color: "#fd7e14" };
    }

    return { key: "not-playing", label: "Not Playing", color: "#dc3545" };
};

const reconcilePlayersWithPool = (players = [], playerPool = []) => {
    if (!Array.isArray(players) || players.length === 0) return [];

    const poolById = new Map(playerPool.map((player) => [getPlayerId(player), player]));
    const poolByKey = new Map(playerPool.map((player) => [buildPlayerLookupKey(player), player]));
    const reconciledPlayers = players.map((player) => {
        const directMatch = poolById.get(getPlayerId(player));
        if (directMatch) return directMatch;

        return poolByKey.get(buildPlayerLookupKey(player)) || player;
    });

    return Array.from(
        new Map(reconciledPlayers.map((player) => [getPlayerId(player) || buildPlayerLookupKey(player), player])).values()
    );
};

const resolveSelectionId = ({
    playerId,
    currentPlayers = [],
    sourcePlayers = [],
}) => {
    if (!playerId) return null;

    const normalizedPlayerId = String(playerId);
    const directMatch = currentPlayers.find((player) => getPlayerId(player) === normalizedPlayerId);
    if (directMatch) {
        return getPlayerId(directMatch);
    }

    const sourcePlayer =
        sourcePlayers.find((player) => getPlayerId(player) === normalizedPlayerId) ||
        null;

    if (!sourcePlayer) return null;

    const reconciledPlayer =
        currentPlayers.find((player) => buildPlayerLookupKey(player) === buildPlayerLookupKey(sourcePlayer)) ||
        currentPlayers.find((player) => String(player?.name || "").trim().toLowerCase() === String(sourcePlayer?.name || "").trim().toLowerCase()) ||
        null;

    return reconciledPlayer ? getPlayerId(reconciledPlayer) : null;
};

function CreateTeam() {
    const navigate = useNavigate();
    const location = useLocation();

    // Data from Navigation
    const { matchData, contestToJoin } = location.state || {};
    const matchId = matchData?.id || matchData?.matchId;
    const incomingSelectedPlayers = location.state?.selectedPlayers || location.state?.team?.players || [];
    const incomingCaptainId =
        location.state?.captainId ||
        location.state?.team?.captainId ||
        null;
    const incomingViceCaptainId =
        location.state?.viceCaptainId ||
        location.state?.vcId ||
        location.state?.team?.viceCaptainId ||
        location.state?.team?.vcId ||
        null;

    // 🔥 INSTANT LOAD LOGIC (CACHING) 🔥
    const storageKey = `draft_team_${matchId}`;
    const cacheKey = `match_data_cache_${matchId}`;

    const getCachedData = () => {
        try {
            const cached = sessionStorage.getItem(cacheKey);
            return cached ? JSON.parse(cached) : null;
        } catch (e) { return null; }
    };
    const cached = getCachedData();
    const shouldUseCachedPlayers = Boolean(
        cached &&
        !(
            isLineupRestrictedStatus(matchData?.status) &&
            !isLineupRestrictedStatus(cached?.status)
        )
    );

    // State Initialization
    const [allPlayers, setAllPlayers] = useState(shouldUseCachedPlayers ? (cached?.players || []) : []);
    const [loading, setLoading] = useState(shouldUseCachedPlayers ? false : true);
    const [lineupOut, setLineupOut] = useState(
        shouldUseCachedPlayers
            ? Boolean(cached?.lineupOut ?? cached?.lineupsOut ?? isLineupOutForMatch(matchData))
            : isLineupOutForMatch(matchData)
    );
    const [teamNames, setTeamNames] = useState(shouldUseCachedPlayers ? (cached?.teamNames || { team1: "T1", team2: "T2" }) : { team1: "T1", team2: "T2" });

    const [errorMsg, setErrorMsg] = useState("");
    const [activeRole, setActiveRole] = useState("WK");
    const [timeLeft, setTimeLeft] = useState("");
    const [errorTimeout, setErrorTimeout] = useState(null);
    const [showDiscardModal, setShowDiscardModal] = useState(false);

    // SORTING STATE
    const [sortBy, setSortBy] = useState({ field: "credits", order: "desc" });

    // 🔥 THE FIX: Smart State Initialization 🔥
    // React state tabhi calculate hoga jab component pehli baar mount hoga
    const [selected, setSelected] = useState(() => {
        try {
            // Priority 1: Explicit navigation data ko sabse pehle respect karo (edit/reload flow)
            if (incomingSelectedPlayers.length > 0) {
                return incomingSelectedPlayers;
            }

            // Priority 1: Agar Draft (Session/Local Storage) me pehle se half-made team hai
            const savedDraft = localStorage.getItem(storageKey);
            if (savedDraft) {
                return JSON.parse(savedDraft);
            }
        } catch (e) {
            console.error("Error loading initial selection", e);
        }

        // Priority 3: Nayi Team
        return [];
    });

    const MAX_PLAYERS = 11;
    const TOTAL_CREDITS = 100;
    const shouldShowPlayingStatus = lineupOut;

    const showError = useCallback((msg) => {
        setErrorMsg(msg);
        setErrorTimeout((prevTimeout) => {
            if (prevTimeout) clearTimeout(prevTimeout);
            return setTimeout(() => setErrorMsg(""), 3000);
        });
    }, []);

// --- TIMER LOGIC (CreateTeam.jsx) ---
    useEffect(() => {
        const matchStartTime = matchData?.matchStartTime || matchData?.startTime || matchData?.date;
        if (!matchStartTime) {
            setTimeLeft("Time TBA");
            return;
        }

        const updateTimer = () => {
            try {
                const parsedMatchTime = parseMatchTime(matchStartTime);
                const start = parsedMatchTime?.getTime();
                const now = new Date().getTime();
                const distance = start - now;

                if (!start || Number.isNaN(start)) {
                    setTimeLeft("Time TBA");
                    return;
                }

                if (distance <= 0) {
                    setTimeLeft("Live");
                    return;
                }

                const totalHours = Math.floor(distance / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                setTimeLeft(`${totalHours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`);
            } catch (e) {
                setTimeLeft("Time TBA");
            }
        };

        updateTimer();
        const interval = setInterval(() => {
            updateTimer();
        }, 1000);

        return () => clearInterval(interval);
    }, [matchData]);

    // 🛑 🆕 THE URL-BLOCKER SECURITY CHECK 🛑
    useEffect(() => {
        const checkExistingTeam = async () => {
            // Agar edit mode hai ya hum contest page se edit dabake aaye hain, toh aage badhne do
            if (location.state?.editMode || location.state?.teamId) return; 

            try {
                const token = localStorage.getItem("token");
                const currentUserId = JSON.parse(localStorage.getItem("user"))?._id;

                if (token && currentUserId && matchId) {
                    const res = await axios.get(`${CRICKET_API_URL}/api/cricket/my-teams/${currentUserId}/${matchId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    const teams = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                    
                    // Agar team already hai, aur user edit karne nahi aaya tha, toh block!
                    if (teams.length >= 1) {
                        alert("Only 1 team per match allowed!");
                        navigate(-1); // Wapas dhakel dega Contests page par
                    }
                }
            } catch (error) {
                console.error("Team check error:", error);
            }
        };

        checkExistingTeam();
    }, [matchId, location.state, navigate]);
    // ----------------------------------------------------

    // --- FETCH DATA ---
    useEffect(() => {
        if (!matchId) return navigate(-1);

        const fetchPlayers = async () => {
            if (!shouldUseCachedPlayers) setLoading(true);
            try {
                const liveResponse = await axios.get(`${CRICKET_API_URL}/api/cricket/match/${matchId}/live`);
                const liveData = liveResponse.data || null;

                if (!liveData) {
                    throw new Error("Failed to load players");
                }

                const team1Name = getShortTeamCode(
                    liveData?.homeTeam?.code,
                    matchData?.team1?.code,
                    liveData?.event_home_team,
                    matchData?.team1?.name,
                    "T1"
                );
                const team2Name = getShortTeamCode(
                    liveData?.awayTeam?.code,
                    matchData?.team2?.code,
                    liveData?.event_away_team,
                    matchData?.team2?.name,
                    "T2"
                );
                const nextMatchStatus = String(
                    liveData?.status?.text ||
                    liveData?.status?.status ||
                    liveData?.status ||
                    matchData?.status ||
                    ""
                ).trim().toLowerCase();
                const nextLineupOut =
                    Boolean(
                        liveData?.lineupOut ??
                        liveData?.lineupsOut ??
                        matchData?.lineupOut ??
                        matchData?.lineupsOut
                    ) || isLineupRestrictedStatus(nextMatchStatus);

                const formatPlayer = (p, teamCode) => {
                    const stablePlayerId = String(
                        p?.playerId ||
                        p?.id ||
                        p?.pid ||
                        `${teamCode}_${String(p?.name || p?.player || "").replace(/\s+/g, "").toLowerCase()}`
                    );
                    const resolvedLineupStatus = normalizeLineupStatus(p?.lineupStatus);
                    const selectionPercentage = normalizeStatNumber(
                        p?.selectionPercentage ??
                        p?.selectionPercent ??
                        p?.selectedByPercentage ??
                        p?.sel
                    );
                    const totalSeriesPoints = normalizeStatNumber(
                        p?.totalSeriesPoints ??
                        p?.seriesPoints ??
                        p?.totalPoints
                    );
                    const totalPoints = normalizeStatNumber(
                        p?.totalPoints ??
                        p?.totalSeriesPoints ??
                        p?.seriesPoints ??
                        p?.points
                    ) ?? 0;
                    const captainPercentage = getPlayerCaptainPercentage(p);
                    const viceCaptainPercentage = getPlayerViceCaptainPercentage(p);

                    const formattedPlayer = {
                        id: stablePlayerId,
                        playerId: stablePlayerId,
                        name: p?.name || p?.player || "Player",
                        team: teamCode,
                        role: normalizeRole(p?.role || p?.player_role),
                        credits: normalizeStatNumber(p?.credits) ?? 0,
                        points: normalizeStatNumber(p?.points) ?? 0,
                        image: p?.image || "https://cdn-icons-png.flaticon.com/512/149/149071.png",
                        sel: selectionPercentage ?? 0,
                        selectionPercentage: selectionPercentage ?? 0,
                        captainPercentage,
                        viceCaptainPercentage,
                        totalPoints,
                        lineupStatus: resolvedLineupStatus,
                        isPlaying: resolvedLineupStatus
                            ? resolvedLineupStatus === "PLAYING"
                            : Boolean(p?.isPlaying),
                        playedLastMatch: Boolean(p?.playedLastMatch)
                    };

                    if (totalSeriesPoints !== null) {
                        formattedPlayer.totalSeriesPoints = totalSeriesPoints;
                    }

                    return formattedPlayer;
                };

                const liveHomePlayers = Array.isArray(liveData?.homeTeam?.players) && liveData.homeTeam.players.length > 0
                    ? liveData.homeTeam.players
                    : liveData?.squads?.homeTeam || [];
                const liveAwayPlayers = Array.isArray(liveData?.awayTeam?.players) && liveData.awayTeam.players.length > 0
                    ? liveData.awayTeam.players
                    : liveData?.squads?.awayTeam || [];

                const homePlayers = liveHomePlayers.map((player) => formatPlayer(player, team1Name));
                const awayPlayers = liveAwayPlayers.map((player) => formatPlayer(player, team2Name));
                    const finalPlayers = Array.from(
                        new Map(
                            [...homePlayers, ...awayPlayers].map((player) => [player.id, player])
                        ).values()
                    );

                    setAllPlayers(finalPlayers);
                    setLineupOut(nextLineupOut);
                    setSelected((prevSelected) => {
                        return reconcilePlayersWithPool(prevSelected, finalPlayers);
                    });
                    setTeamNames({ team1: team1Name, team2: team2Name });

                    sessionStorage.setItem(cacheKey, JSON.stringify({
                        players: finalPlayers,
                        teamNames: { team1: team1Name, team2: team2Name },
                        lineupOut: nextLineupOut,
                        lineupsOut: nextLineupOut,
                        status: nextMatchStatus
                    }));
            } catch (error) {
                if (!shouldUseCachedPlayers) showError("Failed to load players.");
            } finally {
                setLoading(false);
            }
        };
        fetchPlayers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchId]);

    // 🔥 Sync Draft continuously 🔥
    useEffect(() => {
        if (selected.length > 0) {
            localStorage.setItem(storageKey, JSON.stringify(selected));
        } else {
            localStorage.removeItem(storageKey);
        }
    }, [selected, storageKey]);

    // --- TOGGLE PLAYER ---
    const togglePlayer = (player) => {
        if (errorTimeout) clearTimeout(errorTimeout);
        setErrorMsg("");

        const isSelected = selected.some(p => p.id === player.id);

        if (isSelected) {
            setSelected(selected.filter(p => p.id !== player.id));
        } else {
            if (selected.length >= MAX_PLAYERS) return showError("You can only select 11 players!");
            if (selected.reduce((s, p) => s + p.credits, 0) + player.credits > TOTAL_CREDITS) return showError("Not enough credits left!");

            // 10:1 Rule Enforcement
            const teamCount = selected.filter(p => p.team === player.team).length;
            if (teamCount >= 10) return showError(`You can select maximum 10 players from ${player.team}`);

            // New Flexible Role Enforcement (Max 8 of any role, no minimums)
            const roleCount = selected.filter(p => p.role === player.role).length;
            if (roleCount >= 8) return showError(`You can select maximum 8 ${player.role}s`);

            setSelected([...selected, player]);
        }
    };

    // --- SORTING TOGGLE HANDLER ---
    const handleSort = (field) => {
        setSortBy((prev) => ({
            field: field,
            order: prev.field === field && prev.order === "desc" ? "asc" : "desc"
        }));
    };

    // --- SORTING LOGIC ---
    const displayedPlayers = [...allPlayers]
        .filter(p => p.role === activeRole)
        .sort((a, b) => {
            if (lineupOut) {
                const lineupSortDiff = getCreateTeamLineupRank(a) - getCreateTeamLineupRank(b);
                if (lineupSortDiff !== 0) {
                    return lineupSortDiff;
                }
            }

            const valA = getCreateTeamSortValue(a, sortBy.field);
            const valB = getCreateTeamSortValue(b, sortBy.field);
            if (sortBy.order === "desc") {
                return valB - valA;
            } else {
                return valA - valB;
            }
        });

    const t1Count = selected.filter(p => p.team === teamNames.team1).length;
    const t2Count = selected.filter(p => p.team === teamNames.team2).length;
    const tossStatus = String(matchData?.toss || "").trim();
    const playersToPass = buildPlayersToPass(selected);
    const resolvedCaptainId = resolveSelectionId({
        playerId: incomingCaptainId,
        currentPlayers: playersToPass,
        sourcePlayers: incomingSelectedPlayers
    });
    const resolvedViceCaptainId = resolveSelectionId({
        playerId: incomingViceCaptainId,
        currentPlayers: playersToPass,
        sourcePlayers: incomingSelectedPlayers
    });

    const getPlayerStatusItems = (player) =>
        shouldShowPlayingStatus
            ? [getCreateTeamPlayerStatusItem(player)]
            : [];

    // 🔥 BACK BUTTON LOGIC
    const handleBackClick = () => {
        if (selected.length > 0) {
            setShowDiscardModal(true);
        } else {
            navigate(-1);
        }
    };

    // 🔥 INSTANT DISCARD LOGIC 🔥
    const handleDiscardTeam = () => {
        localStorage.removeItem(`draft_team_${matchId}`);
        sessionStorage.removeItem(`draft_cvc_${matchId}`);
        navigate(-1);
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center min-vh-100 bg-white">
            <div className="spinner-border" style={{ color: "#800000" }} role="status"></div>
        </div>
    );

    return (
        <div className="bg-white min-vh-100 pb-5" style={{ fontFamily: "Roboto, sans-serif" }}>

            {/* === 1. TOP MAROON HEADER === */}
            <div className="sticky-top bg-white" style={{ zIndex: 1040 }}>
                {/* 🔥 Maroon Gradient Applied */}
                <div style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)" }} className="text-white pt-2 pb-3 shadow-sm">

                    {/* Nav Bar */}
                    <div className="d-flex justify-content-between align-items-center px-3 mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <i className="bi bi-arrow-left fs-4" onClick={handleBackClick} style={{ cursor: 'pointer' }}></i>
                            <div>
                                <h6 className="mb-0 fw-bold">Create Team</h6>
                                <small className="opacity-75" style={{ fontSize: "12px" }}>{timeLeft}</small>
                            </div>
                        </div>
                        <div className="rounded-circle border border-white border-opacity-50 d-flex align-items-center justify-content-center"
                            style={{ width: '35px', height: '35px', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer', backgroundColor: "rgba(255,255,255,0.1)" }}
                            onClick={() => navigate('/point-system')}>
                            PTS
                        </div>
                    </div>

                    {/* Match Score & Team Counts */}
                    <div className={`d-flex justify-content-center align-items-center gap-3 ${tossStatus ? "mb-1" : "mb-3"}`}>
                        <div className="d-flex align-items-center gap-2">
                            <img src={matchData?.team1?.image || "https://cdn-icons-png.flaticon.com/512/3344/3344394.png"} alt="T1" className="bg-white rounded-circle" style={{ width: '28px', height: '28px', padding: "2px" }} />
                            <span className="fw-bold text-white" style={{ fontSize: "14px" }}>{teamNames.team1}</span>
                        </div>

                        <div className="fw-bold px-3 py-1 rounded-pill" style={{ fontSize: "15px", backgroundColor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)" }}>
                            <span className="text-white">{t1Count}</span>
                            <span className="mx-2 text-white-50">-</span>
                            <span className="text-white">{t2Count}</span>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <span className="fw-bold text-white" style={{ fontSize: "14px" }}>{teamNames.team2}</span>
                            <img src={matchData?.team2?.image || "https://cdn-icons-png.flaticon.com/512/3344/3344394.png"} alt="T2" className="bg-white rounded-circle" style={{ width: '28px', height: '28px', padding: "2px" }} />
                        </div>
                    </div>
                    {tossStatus && (
                        <div className="text-center mb-3 opacity-75" style={{ fontSize: "12px" }}>
                            {tossStatus}
                        </div>
                    )}

                    {/* Slanted Progress Bar */}
                    <div className="d-flex align-items-center px-3">
                        <span className="fw-bold text-white me-2" style={{ fontSize: '12px' }}>{selected.length}/11</span>
                        <div className="d-flex flex-grow-1 gap-1 mx-2">
                            {[...Array(11)].map((_, i) => (
                                <div key={i} style={{
                                    height: "5px", flexGrow: 1, borderRadius: "2px",
                                    backgroundColor: i < selected.length ? "#ffd700" : "rgba(255,255,255,0.2)",
                                    transform: "skewX(-20deg)"
                                }} />
                            ))}
                        </div>
                        <i className="bi bi-arrow-counterclockwise fs-5 text-white opacity-75" style={{ cursor: "pointer" }} onClick={() => setSelected([])}></i>
                    </div>
                </div>

                {lineupOut ? (
                    <div className="bg-success-subtle text-success text-center py-1 fw-bold small">
                        <i className="bi bi-check-circle-fill me-1"></i> Lineups Out
                    </div>
                ) : (
                    <div className="bg-warning-subtle text-warning text-center py-1 fw-bold small">
                        <i className="bi bi-clock me-1"></i> Lineups Not Announced Yet
                    </div>
                )}

                {/* === 2. TABS WITH COUNTS === */}
                <div className="d-flex text-center bg-white border-bottom shadow-sm">
                    {["WK", "BAT", "AR", "BOWL"].map(role => {
                        const count = selected.filter(p => p.role === role).length;
                        const isActive = activeRole === role;

                        return (
                            <div key={role} onClick={() => setActiveRole(role)}
                                className="flex-grow-1 py-3 position-relative"
                                style={{ cursor: "pointer", color: isActive ? "#800000" : "#6c757d", transition: "color 0.2s" }}>
                                <div className="fw-bold d-flex justify-content-center align-items-center gap-1" style={{ fontSize: "14px" }}>
                                    {role}
                                    {/* 🔥 TAB COUNTS UI */}
                                    {count > 0 && <span style={{ fontSize: "12px", color: isActive ? "#800000" : "#adb5bd" }}>({count})</span>}
                                </div>
                                {isActive && <div className="position-absolute bottom-0 start-50 translate-middle-x" style={{ height: '3px', width: '50%', backgroundColor: '#800000', borderRadius: '3px 3px 0 0' }} />}
                            </div>
                        )
                    })}
                </div>

                {/* === 3. FILTER CHIPS === */}
                <div className="d-flex gap-2 px-3 py-2 bg-light border-bottom overflow-auto" style={{ whiteSpace: "nowrap", scrollbarWidth: "none" }}>
                    <button className={`btn btn-sm rounded-pill px-3 ${sortBy.field === 'credits' ? 'bg-white fw-bold shadow-sm border' : 'border text-muted'}`}
                        style={{ borderColor: sortBy.field === 'credits' ? '#800000' : '#ddd', color: sortBy.field === 'credits' ? '#800000' : 'inherit' }}
                        onClick={() => handleSort("credits")}>
                        Credits {sortBy.field === 'credits' ? (sortBy.order === 'desc' ? '↓' : '↑') : ''}
                    </button>
                    <button className={`btn btn-sm rounded-pill px-3 ${sortBy.field === 'sel' ? 'bg-white fw-bold shadow-sm border' : 'border text-muted'}`}
                        style={{ borderColor: sortBy.field === 'sel' ? '#800000' : '#ddd', color: sortBy.field === 'sel' ? '#800000' : 'inherit' }}
                        onClick={() => handleSort("sel")}>
                        % Selected By {sortBy.field === 'sel' ? (sortBy.order === 'desc' ? '↓' : '↑') : ''}
                    </button>
                    <button className={`btn btn-sm rounded-pill px-3 ${sortBy.field === 'points' ? 'bg-white fw-bold shadow-sm border' : 'border text-muted'}`}
                        style={{ borderColor: sortBy.field === 'points' ? '#800000' : '#ddd', color: sortBy.field === 'points' ? '#800000' : 'inherit' }}
                        onClick={() => handleSort("points")}>
                        Points {sortBy.field === 'points' ? (sortBy.order === 'desc' ? '↓' : '↑') : ''}
                    </button>
                </div>

                {/* === 4. LIST HEADER === */}
                <div className="d-flex px-3 py-2 bg-white text-muted border-bottom align-items-center" style={{ fontSize: '11px' }}>
                    <div style={{ width: "60px" }}></div>
                    <div className="flex-grow-1 fw-bold">% Sel by</div>
                    <div className="text-center fw-bold" style={{ width: "50px" }}>Points</div>
                    <div className="text-center fw-bold" style={{ width: "50px" }}>Credits</div>
                    <div style={{ width: "40px" }}></div>
                </div>
            </div>

            {/* === ERROR TOAST === */}
            {errorMsg && (
                <div className="position-fixed w-100 d-flex justify-content-center" style={{ top: "250px", zIndex: 1050 }}>
                    <div className="text-white text-center py-2 px-4 rounded-pill fw-bold shadow-lg" style={{ fontSize: "12px", backgroundColor: "#dc3545" }}>
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>{errorMsg}
                    </div>
                </div>
            )}

            {/* === 5. PLAYER LIST === */}
            <div className="bg-white" style={{ paddingBottom: "90px" }}>
                {displayedPlayers.length === 0 ? (
                    <div className="text-center py-5 text-muted fw-bold small">No players found in this category.</div>
                ) : (
                    displayedPlayers.map((player, index) => {
                        const isSelected = selected.some(p => p.id === player.id);
                        const currentIsActiveLineupPlayer = isActiveLineupPlayer(player);
                        const previousIsActiveLineupPlayer =
                            index > 0 ? isActiveLineupPlayer(displayedPlayers[index - 1]) : true;
                        const shouldShowBenchDivider =
                            lineupOut &&
                            !currentIsActiveLineupPlayer &&
                            previousIsActiveLineupPlayer;

                        return (
                            <React.Fragment key={player.id}>
                                {shouldShowBenchDivider && (
                                    <div className="w-100 text-center fw-bold bg-light py-1 my-2 text-muted border-top border-bottom" style={{ fontSize: '11px' }}>
                                        --- BENCH ---
                                    </div>
                                )}

                                <div onClick={() => togglePlayer(player)}
                                    className={`d-flex align-items-center px-3 py-2 border-bottom ${isSelected ? "bg-danger bg-opacity-10" : ""}`}
                                    style={{ cursor: "pointer", borderLeft: isSelected ? "4px solid #800000" : "4px solid transparent", transition: "all 0.2s" }}>

                                    {/* Player Image & Badges */}
                                    <div className="position-relative me-3" style={{ width: '50px', height: '50px' }}>
                                        <i className="bi bi-info-circle-fill position-absolute bg-white rounded-circle text-secondary"
                                            style={{ top: '-2px', left: '-5px', fontSize: '12px', zIndex: 2 }}></i>
                                        <img src={player.image} alt={player.name}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png"} />
                                        <span className={`position-absolute badge ${player.team === teamNames.team1 ? 'bg-light text-dark' : 'bg-dark text-white'} border shadow-sm`}
                                            style={{ bottom: '-5px', left: '-5px', fontSize: '8px', padding: "2px 4px" }}>
                                            {player.team}
                                        </span>
                                    </div>
                                    
                                    {/* Name & Sel % */}
                                    <div className="flex-grow-1">
                                        <div className="d-flex align-items-center gap-2 flex-wrap" style={{ minWidth: 0 }}>
                                            <h6 className="mb-0 fw-bold text-dark text-truncate" style={{ fontSize: "14px", maxWidth: "100%" }}>{player.name}</h6>
                                            {getPlayerStatusItems(player).map(status => (
                                                <span key={status.key} className="d-inline-flex align-items-center gap-1" style={{ fontSize: "10px", lineHeight: 1 }}>
                                                    <span className="rounded-circle flex-shrink-0" style={{ width: "7px", height: "7px", backgroundColor: status.color }}></span>
                                                    <span className="fw-semibold" style={{ color: status.color }}>{status.label}</span>
                                                </span>
                                            ))}
                                        </div>
                                        <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                                            {formatStatNumber(getPlayerSelectionPercentage(player))}%
                                        </small>
                                    </div>

                                    {/* Points */}
                                    <div className="text-center text-muted" style={{ width: "50px", fontSize: "13px" }}>
                                        {formatStatNumber(getPlayerTotalSeriesPoints(player))}
                                    </div>

                                    {/* Credits */}
                                    <div className="text-center fw-bold text-dark" style={{ width: "50px", fontSize: "14px" }}>
                                        {player.credits}
                                    </div>

                                    {/* Action Icon (Maroon Theme) */}
                                    <div className="text-end" style={{ width: "40px" }}>
                                        {isSelected ? (
                                            <i className="bi bi-dash-circle-fill fs-4" style={{ color: "#dc3545" }}></i>
                                        ) : (
                                            <i className="bi bi-plus-circle fs-4" style={{ color: "#198754" }}></i>
                                        )}
                                    </div>
                                </div>
                            </React.Fragment>
                        );
                    })
                )}
            </div>

            {/* === 6. BOTTOM ACTION BAR === */}
            <div className="fixed-bottom bg-white p-3 shadow-lg d-flex gap-3 align-items-center justify-content-center" style={{ zIndex: 1040, borderTop: "1px solid #e5e7eb" }}>

                {/* PREVIEW BUTTON */}
                <button className="btn fw-bold text-dark d-flex align-items-center justify-content-center gap-2 bg-white"
                    style={{ flex: 1, border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", padding: "12px 0", letterSpacing: "0.5px" }}
                    onClick={() => navigate("/team-preview", {
                        state: {
                            team: {
                                players: playersToPass,
                                captainId: resolvedCaptainId,
                                viceCaptainId: resolvedViceCaptainId
                            },
                            teamNames,
                            matchData: {
                                ...matchData,
                                lineupOut,
                                lineupsOut: lineupOut
                            },
                            teamId: location.state?.teamId
                        }
                    })}>
                    <i className="bi bi-eye fs-5"></i> PREVIEW
                </button>

                {/* NEXT BUTTON */}
                <button className={`btn fw-bold ${selected.length === 11 ? 'text-white' : 'text-muted'}`}
                    style={{ flex: 1, background: selected.length === 11 ? "linear-gradient(135deg, #800000 0%, #4A0000 100%)" : "#e5e7eb", border: "none", borderRadius: "8px", fontSize: "14px", padding: "12px 0", letterSpacing: "0.5px" }}
                    onClick={() => navigate("/choose-captain", {
                        state: {
                            selectedPlayers: playersToPass,
                            team: {
                                players: playersToPass,
                                captainId: resolvedCaptainId,
                                viceCaptainId: resolvedViceCaptainId
                            },
                            matchData: {
                                ...matchData,
                                lineupOut,
                                lineupsOut: lineupOut
                            },
                            contestToJoin,
                            editMode: location.state?.editMode,
                            teamId: location.state?.teamId,
                            captainId: resolvedCaptainId,
                            viceCaptainId: resolvedViceCaptainId
                        }
                    })}
                    disabled={selected.length !== 11}>
                    NEXT
                </button>
            </div>

            {/* === DISCARD MODAL === */}
            {showDiscardModal && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1060 }}>
                    <div className="card border-0 shadow-lg p-4 bg-white" style={{ maxWidth: '340px', width: '90%', borderRadius: "12px" }}>
                        <div className="text-center">
                            <h5 className="fw-bold mb-3" style={{ color: "#1e293b", fontSize: "18px" }}>Go Back?</h5>
                            <div className="mx-auto mb-3" style={{
                                width: '64px', height: '64px', backgroundColor: '#f1f5f9',
                                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <i className="bi bi-exclamation-circle" style={{ color: "#800000", fontSize: "30px", display: "flex" }}></i>
                            </div>
                            <p className="text-muted mb-4" style={{ fontSize: "15px" }}>This Team will not be saved!</p>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <button className="btn w-100 fw-bold text-white shadow-sm" onClick={handleDiscardTeam} style={{ backgroundColor: "#dc3545", borderRadius: "6px", padding: "12px 0", fontSize: "14px", letterSpacing: "0.5px" }}>
                                DISCARD TEAM
                            </button>
                            <button className="btn w-100 fw-bold bg-white" onClick={() => setShowDiscardModal(false)} style={{ border: "1px solid #cbd5e1", color: "#1e293b", borderRadius: "6px", padding: "12px 0", fontSize: "14px", letterSpacing: "0.5px" }}>
                                CONTINUE EDITING
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CreateTeam;
